import { useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import AlertMessage from '../ui/AlertMessage';
import {
  ClaimProvider,
  ClaimProviderUpdatePayload,
} from '../../models/claimProvider';

export type ClaimProviderFormData = {
  name: string;
  emails: string[];
  phone: string;
  website: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  sportId: string;
  rating: string;
  reviewsCount: string;
  imageUrl: string;
  googleMapsUrl: string;
};

type Props = {
  provider: ClaimProvider;
  isSubmitting: boolean;
  submissionError?: string;
  submissionSuccess?: string;
  onCancel: () => void;
  onSubmit: (payload: ClaimProviderUpdatePayload) => void;
};

const toFormData = (provider: ClaimProvider): ClaimProviderFormData => ({
  name: provider.name || '',
  emails: provider.emails?.length ? [...provider.emails] : [''],
  phone: provider.phone || '',
  website: provider.website || '',
  address: provider.address || '',
  city: provider.city || '',
  state: provider.state || '',
  country: provider.country || '',
  postalCode: provider.postalCode || '',
  sportId: provider.sportId || '',
  rating:
    provider.rating !== null && provider.rating !== undefined
      ? String(provider.rating)
      : '',
  reviewsCount:
    provider.reviewsCount !== null && provider.reviewsCount !== undefined
      ? String(provider.reviewsCount)
      : '',
  imageUrl: provider.imageUrl || '',
  googleMapsUrl: provider.googleMapsUrl || '',
});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ClaimProviderForm = ({
  provider,
  isSubmitting,
  submissionError,
  submissionSuccess,
  onCancel,
  onSubmit,
}: Props) => {
  const initial = useMemo(() => toFormData(provider), [provider]);
  const [formData, setFormData] = useState<ClaimProviderFormData>(initial);
  const [validationError, setValidationError] = useState<string>();

  useEffect(() => {
    setFormData(toFormData(provider));
  }, [provider]);

  const updateField = <K extends keyof ClaimProviderFormData>(
    key: K,
    value: ClaimProviderFormData[K],
  ) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const updateEmail = (index: number, value: string) => {
    setFormData(prev => {
      const emails = [...prev.emails];
      emails[index] = value;
      return { ...prev, emails };
    });
  };

  const addEmail = () => {
    setFormData(prev => ({ ...prev, emails: [...prev.emails, ''] }));
  };

  const removeEmail = (index: number) => {
    setFormData(prev => {
      const emails = prev.emails.filter((_, i) => i !== index);
      return { ...prev, emails: emails.length ? emails : [''] };
    });
  };

  const buildDirtyPayload = (): ClaimProviderUpdatePayload | null => {
    const payload: ClaimProviderUpdatePayload = {};

    if (formData.name.trim() !== initial.name.trim()) {
      payload.name = formData.name.trim() || null;
    }

    const cleanedEmails = formData.emails
      .map(email => email.trim())
      .filter(Boolean);
    const initialEmails = initial.emails.map(email => email.trim()).filter(Boolean);
    if (
      cleanedEmails.length !== initialEmails.length ||
      cleanedEmails.some((email, index) => email !== initialEmails[index])
    ) {
      payload.emails = cleanedEmails;
    }

    const stringFields: Array<
      keyof Pick<
        ClaimProviderFormData,
        | 'phone'
        | 'website'
        | 'address'
        | 'city'
        | 'state'
        | 'country'
        | 'postalCode'
        | 'sportId'
        | 'imageUrl'
        | 'googleMapsUrl'
      >
    > = [
      'phone',
      'website',
      'address',
      'city',
      'state',
      'country',
      'postalCode',
      'sportId',
      'imageUrl',
      'googleMapsUrl',
    ];

    stringFields.forEach(field => {
      const next = formData[field].trim();
      const prev = initial[field].trim();
      if (next !== prev) {
        payload[field] = next || null;
      }
    });

    if (formData.rating.trim() !== initial.rating.trim()) {
      if (!formData.rating.trim()) {
        payload.rating = null;
      } else {
        const rating = Number(formData.rating);
        payload.rating = rating;
      }
    }

    if (formData.reviewsCount.trim() !== initial.reviewsCount.trim()) {
      if (!formData.reviewsCount.trim()) {
        payload.reviewsCount = null;
      } else {
        const reviewsCount = Number(formData.reviewsCount);
        payload.reviewsCount = reviewsCount;
      }
    }

    return Object.keys(payload).length ? payload : null;
  };

  const validate = (payload: ClaimProviderUpdatePayload) => {
    if (payload.name !== undefined && !payload.name) {
      return 'Provider name cannot be empty.';
    }
    if (payload.emails) {
      const invalid = payload.emails.find(email => !emailRegex.test(email));
      if (invalid) {
        return `Invalid email: ${invalid}`;
      }
    }
    if (payload.rating !== undefined && payload.rating !== null) {
      if (Number.isNaN(payload.rating) || payload.rating < 0 || payload.rating > 5) {
        return 'Rating must be a number between 0 and 5.';
      }
    }
    if (payload.reviewsCount !== undefined && payload.reviewsCount !== null) {
      if (
        Number.isNaN(payload.reviewsCount) ||
        !Number.isInteger(payload.reviewsCount) ||
        payload.reviewsCount < 0
      ) {
        return 'Reviews count must be a non-negative integer.';
      }
    }
    const urlFields: Array<'website' | 'imageUrl' | 'googleMapsUrl'> = [
      'website',
      'imageUrl',
      'googleMapsUrl',
    ];
    for (const field of urlFields) {
      const value = payload[field];
      if (value) {
        try {
          // Accept protocol-relative-looking hosts by normalizing for check.
          const candidate = /^https?:\/\//i.test(value)
            ? value
            : `https://${value}`;
          // eslint-disable-next-line no-new
          new URL(candidate);
        } catch {
          return `Invalid ${field}. Include a valid URL (e.g. https://example.com).`;
        }
      }
    }
    return undefined;
  };

  const handleSubmit = () => {
    const payload = buildDirtyPayload();
    if (!payload) {
      setValidationError('No changes to save.');
      return;
    }
    const error = validate(payload);
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(undefined);
    onSubmit(payload);
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm space-y-6">
      <h1 className="text-3xl font-semibold text-secondary-main">
        Edit {provider.name}
      </h1>

      {(validationError || submissionError) && (
        <AlertMessage
          type="error"
          message={validationError || submissionError || ''}
        />
      )}
      {submissionSuccess && (
        <AlertMessage type="success" message={submissionSuccess} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          placeholder="Name"
          value={formData.name}
          onInput={value => updateField('name', value)}
          required
        />
        <Input
          placeholder="Sport ID (e.g. tennis)"
          value={formData.sportId}
          onInput={value => updateField('sportId', value)}
        />
        <Input
          placeholder="Phone"
          value={formData.phone}
          onInput={value => updateField('phone', value)}
        />
        <Input
          placeholder="Website"
          value={formData.website}
          onInput={value => updateField('website', value)}
        />
        <Input
          placeholder="Address"
          value={formData.address}
          onInput={value => updateField('address', value)}
        />
        <Input
          placeholder="City"
          value={formData.city}
          onInput={value => updateField('city', value)}
        />
        <Input
          placeholder="State"
          value={formData.state}
          onInput={value => updateField('state', value)}
        />
        <Input
          placeholder="Country"
          value={formData.country}
          onInput={value => updateField('country', value)}
        />
        <Input
          placeholder="Postal code"
          value={formData.postalCode}
          onInput={value => updateField('postalCode', value)}
        />
        <Input
          placeholder="Rating (0-5)"
          type="number"
          value={formData.rating}
          onInput={value => updateField('rating', value)}
        />
        <Input
          placeholder="Reviews count"
          type="number"
          value={formData.reviewsCount}
          onInput={value => updateField('reviewsCount', value)}
        />
        <Input
          placeholder="Image URL"
          value={formData.imageUrl}
          onInput={value => updateField('imageUrl', value)}
        />
        <Input
          placeholder="Google Maps URL"
          value={formData.googleMapsUrl}
          onInput={value => updateField('googleMapsUrl', value)}
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-secondary-main">Emails</h2>
          <Button
            text="Add email"
            styleType="secondary"
            className="w-auto"
            onClick={addEmail}
          />
        </div>
        {formData.emails.map((email, index) => (
          <div key={`email-${index}`} className="flex gap-3 items-end">
            <Input
              placeholder={`Email ${index + 1}`}
              value={email}
              onInput={value => updateEmail(index, value)}
            />
            <Button
              text="Remove"
              styleType="secondary"
              className="w-auto"
              onClick={() => removeEmail(index)}
            />
          </div>
        ))}
      </div>

      <div className="flex gap-4 max-w-md">
        <Button
          text="Save changes"
          showLoader={isSubmitting}
          disabled={isSubmitting}
          onClick={handleSubmit}
        />
        <Button text="Cancel" styleType="secondary" onClick={onCancel} />
      </div>
    </div>
  );
};

export default ClaimProviderForm;
